import { type MatchKind, suggestSectionMapping } from '@digitalshare/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, mappingApi, type Section, sectionsApi } from '../lib/api.ts';
import { Button } from './ui.tsx';

/**
 * Mapeo de secciones de un sitio.
 *
 * Es la pantalla que resuelve el muro #8: una fila por sección canónica y un
 * desplegable con las categorías reales de **ese** periódico.
 *
 * Tres cosas que la pantalla tiene que dejar claras y no son obvias:
 *
 * 1. **Qué secciones se quedan sin mapear.** Publicar con una de ellas en este
 *    sitio fallaría, así que hay que verlo antes de intentarlo, no después.
 * 2. **Qué mapeos apuntan a una categoría que ya no existe.** Alguien la borró
 *    en el WordPress y el panel no puede saberlo hasta que lo compruebe.
 * 3. **Cuándo se leyeron las categorías.** Si alguien acaba de crear una en el
 *    periódico y no aparece, la respuesta es "comprueba el sitio", no "está
 *    roto".
 */
export function SectionMapping({ siteId }: { siteId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  /** Cambios sin guardar: sectionId → wpTermId (o null para desmapear). */
  const [draft, setDraft] = useState<Record<string, number | null>>({});

  const sectionsQuery = useQuery({ queryKey: ['sections'], queryFn: sectionsApi.list });
  const mappingQuery = useQuery({
    queryKey: ['mapping', siteId],
    queryFn: () => mappingApi.get(siteId),
  });

  // Al recargar los datos del servidor se descartan los cambios locales: lo que
  // hay en pantalla vuelve a ser lo que está guardado.
  useEffect(() => {
    setDraft({});
  }, []);

  const save = useMutation({
    mutationFn: () =>
      mappingApi.save(
        siteId,
        Object.entries(draft).map(([sectionId, wpTermId]) => ({ sectionId, wpTermId })),
      ),
    onSuccess: () => {
      setDraft({});
      void queryClient.invalidateQueries({ queryKey: ['mapping', siteId] });
    },
  });

  const sections = sectionsQuery.data?.sections ?? [];
  const mapping = mappingQuery.data;

  /**
   * Coincidencias por nombre para lo que todavía no está mapeado.
   *
   * Se calculan, se enseñan y **no se aplican solas**. Un mapeo equivocado
   * publica la noticia en la sección que no es, en el periódico de un cliente,
   * y eso no se deshace desde aquí.
   *
   * Va aquí arriba, **antes de los `return` tempranos**: los hooks tienen que
   * ejecutarse en el mismo orden en cada render, y si esto quedara debajo,
   * pasar de "cargando" a "cargado" cambiaría el número de hooks y React
   * fallaría. Por eso tolera que `mapping` todavía no exista.
   */
  const suggestions = useMemo(
    () =>
      mapping
        ? suggestSectionMapping({
            sections,
            categories: mapping.categories,
            alreadyMapped: new Set(mapping.mappings.map((entry) => entry.sectionId)),
          })
        : [],
    [sections, mapping],
  );

  if (mappingQuery.isLoading || sectionsQuery.isLoading) {
    return <p className="text-sm text-[var(--color-muted)]">{t('common.loading')}</p>;
  }

  if (!mapping) {
    return <p className="text-sm text-[var(--color-danger)]">{t('errors.generic')}</p>;
  }

  if (sections.length === 0) {
    return (
      <p className="rounded-lg bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-muted)]">
        {t('mapping.noSections')}
      </p>
    );
  }

  const current = new Map(mapping.mappings.map((entry) => [entry.sectionId, entry]));
  const hasChanges = Object.keys(draft).length > 0;

  const suggestionBySection = new Map(suggestions.map((entry) => [entry.sectionId, entry]));

  // Solo las que siguen sin tocar: en cuanto alguien elige algo a mano, su
  // sugerencia deja de tener sentido.
  const pending = suggestions.filter((entry) => !(entry.sectionId in draft));

  const applySuggestions = () => {
    setDraft((previous) => {
      const next = { ...previous };

      for (const entry of pending) {
        next[entry.sectionId] = entry.wpTermId;
      }

      return next;
    });
  };

  /** Valor a mostrar: lo editado si lo hay, si no lo guardado. */
  const valueFor = (section: Section): number | null =>
    section.id in draft ? (draft[section.id] ?? null) : (current.get(section.id)?.wpTermId ?? null);

  const staleCount = mapping.mappings.filter((entry) => entry.stale).length;

  return (
    <div>
      {mapping.categories.length === 0 ? (
        <p className="mb-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {t('mapping.noCategories')}
        </p>
      ) : null}

      {staleCount > 0 ? (
        <p className="mb-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {t('mapping.staleWarning', { count: staleCount })}
        </p>
      ) : null}

      {/*
        Sugerencias por nombre.

        Aparece solo cuando hay algo que proponer. El botón rellena los
        desplegables pero NO guarda: el botón de guardar sigue siendo el único
        que escribe, y así se revisa lo propuesto antes de confirmarlo.
      */}
      {pending.length > 0 ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-blue-50 px-4 py-3">
          <p className="text-sm text-blue-900">
            {pending.length === 1
              ? 'Una categoría de este sitio se llama igual que una de las tuyas.'
              : `${pending.length} categorías de este sitio se llaman igual que las tuyas.`}{' '}
            <span className="text-blue-700">Revisa lo propuesto antes de guardar.</span>
          </p>

          <button
            type="button"
            onClick={applySuggestions}
            className="shrink-0 rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-sm font-medium text-blue-900 hover:bg-blue-100"
          >
            Rellenar {pending.length === 1 ? 'la sugerencia' : 'las sugerencias'}
          </button>
        </div>
      ) : null}

      {save.error instanceof ApiError ? (
        <p className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-[var(--color-danger)]">
          {save.error.message}
        </p>
      ) : null}

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--color-line)] text-left text-xs text-[var(--color-muted)]">
            <th className="pb-2 font-medium">{t('mapping.section')}</th>
            <th className="pb-2 font-medium">{t('mapping.category')}</th>
          </tr>
        </thead>
        <tbody>
          {sections.map((section) => {
            const entry = current.get(section.id);
            const value = valueFor(section);
            const unmapped = value === null;

            const suggestion = suggestionBySection.get(section.id);

            // Está puesto por una sugerencia y todavía no se ha guardado. Es lo
            // que distingue "esto lo decidiste tú" de "esto lo propuso el
            // panel", que es la diferencia que hay que poder ver antes de
            // pulsar Guardar.
            const isSuggested = Boolean(suggestion && draft[section.id] === suggestion.wpTermId);

            return (
              <tr key={section.id} className="border-b border-[var(--color-line)] last:border-0">
                <td className="py-2.5 pr-4">
                  <span className={unmapped ? 'text-[var(--color-muted)]' : ''}>{section.name}</span>
                  {entry?.stale ? (
                    <span className="ml-2 text-xs text-amber-700">{t('mapping.stale')}</span>
                  ) : null}

                  {isSuggested ? <SuggestionTag match={suggestion?.match} /> : null}
                </td>

                <td className="py-2.5">
                  <select
                    value={value ?? ''}
                    onChange={(event) =>
                      setDraft((previous) => ({
                        ...previous,
                        [section.id]: event.target.value === '' ? null : Number(event.target.value),
                      }))
                    }
                    disabled={mapping.categories.length === 0}
                    className={`w-full rounded border px-2 py-1.5 text-sm ${
                      unmapped
                        ? 'border-amber-300 bg-amber-50 text-amber-800'
                        : isSuggested
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-[var(--color-line)]'
                    }`}
                  >
                    <option value="">{t('mapping.unmapped')}</option>
                    {mapping.categories.map((category) => (
                      <option key={category.wpTermId} value={category.wpTermId}>
                        {/* El sangrado hace visible la jerarquía: "Deportes >
                            Béisbol" no es lo mismo que "Béisbol" suelto. */}
                        {category.parentWpId > 0 ? `— ${category.name}` : category.name}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-4 flex items-center justify-between gap-4">
        <p className="text-xs text-[var(--color-muted)]">
          {mapping.categoriesFetchedAt
            ? t('mapping.fetchedAt', {
                time: new Date(mapping.categoriesFetchedAt).toLocaleString('es-DO', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                }),
              })
            : t('mapping.neverFetched')}
        </p>

        <div className="w-40 shrink-0">
          <Button type="button" onClick={() => save.mutate()} loading={save.isPending} disabled={!hasChanges}>
            {save.isPending ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Marca una fila como propuesta por el panel y todavía sin guardar.
 *
 * Las de plural se distinguen de las exactas a propósito: "Nacional" y
 * "Nacionales" casi siempre son lo mismo, pero no siempre —hay periódicos con
 * "Nacional" para el equipo de fútbol— y esa es justo la que hay que mirar dos
 * veces antes de guardar.
 */
function SuggestionTag({ match }: { match: MatchKind | undefined }) {
  return (
    <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
      {match === 'plural' ? 'sugerido (singular/plural)' : 'sugerido'}
    </span>
  );
}
